import { BrowserRouter, Routes, Route, useLocation, Link, Navigate } from "react-router-dom";
import "@/index.css";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "sonner";
import Nav from "@/components/Nav";
import GoogleOneTap from "@/components/GoogleOneTap";
import Protected from "@/components/Protected";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import Pricing from "@/pages/Pricing";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refund from "@/pages/Refund";
import Security from "@/pages/Security";
import Countries from "@/pages/Countries";
// iter104 — After founder approval on 2026-08-26 the France prototype
// (CountryDetailFrance.jsx) is promoted to the shared dossier layout for
// all 19 countries. Legacy CountryDetail.jsx retained as dead code until
// cleanup in a future refactor pass.
import CountryDossier from "@/pages/CountryDetailFrance";
import Universities from "@/pages/Universities";
import PostArrival from "@/pages/PostArrival";
import UniversityDetail from "@/pages/UniversityDetail";
import Partnerships from "@/pages/Partnerships";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Intake from "@/pages/Intake";
import Dashboard from "@/pages/Dashboard";
import Manifest from "@/pages/Manifest";
import Documents from "@/pages/Documents";
import Packing from "@/pages/Packing";
import Roam from "@/pages/Roam";
import Connect from "@/pages/Connect";
import CostEstimator from "@/pages/CostEstimator";
import Admin from "@/pages/Admin";
import Handler from "@/pages/Handler";
import NotFound from "@/pages/NotFound";
import Contact from "@/pages/Contact";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MomentPublic from "@/pages/MomentPublic";
import TelemetryProvider from "@/components/TelemetryProvider";
import SiteFooter from "@/components/SiteFooter";

function RoutedShell() {
  const loc = useLocation();
  return (
    <TelemetryProvider>
    <div className="min-h-screen flex flex-col" style={{ background: "var(--moe-base)" }}>
      <ScrollToTop />
      <Nav />
      <GoogleOneTap />
      <main key={loc.pathname} className="flex-1 moe-fade-up">
        <Routes location={loc}>
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<Pricing />} />
          {/* iter93 (Items JJ / KK / MM) — public legal / policy pages */}
          <Route path="/terms"                element={<Terms />} />
          <Route path="/privacy"              element={<Privacy />} />
          <Route path="/refund-cancellation"  element={<Refund />} />
          <Route path="/security"             element={<Security />} />
          <Route path="/countries" element={<Countries />} />
          {/* iter104 — All 19 country dossiers now route through the shared
              v2 layout. Founder-approved 2026-08-26 after France prototype
              review. */}
          <Route path="/countries/:country" element={<CountryDossier />} />
          <Route path="/countries/:country/post-arrival" element={<PostArrival />} />
          <Route path="/universities" element={<Universities />} />
          <Route path="/universities/:school" element={<UniversityDetail />} />
          <Route path="/partnerships" element={<Partnerships />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/start" element={<Protected roles={["student"]}><Intake /></Protected>} />
          <Route path="/dashboard" element={<Protected roles={["student"]}><Dashboard /></Protected>} />
          <Route path="/dashboard/manifest" element={<Protected roles={["student"]}><Manifest /></Protected>} />
          {/* iter93 (Item HH) — Moments promoted to a top-level sibling of
             Manifest, per real user feedback. Mounts the same Manifest page
             with the Moments tab preselected; deep-links like
             /dashboard/manifest?tab=moments still work for backward
             compatibility. */}
          <Route path="/dashboard/moments" element={<Protected roles={["student"]}><Manifest defaultTab="moments" /></Protected>} />
          <Route path="/dashboard/documents" element={<Protected roles={["student"]}><Documents /></Protected>} />
          <Route path="/dashboard/packing" element={<Protected roles={["student"]}><Packing /></Protected>} />
          <Route path="/dashboard/roam" element={<Protected roles={["student"]}><Roam /></Protected>} />
          <Route path="/dashboard/connect" element={<Protected roles={["student"]}><Connect /></Protected>} />
          <Route path="/dashboard/cost-estimator" element={<Protected roles={["student"]}><CostEstimator /></Protected>} />
          {/* iter86 - /dashboard/settings retired; delete-account lives on the Dashboard. Redirect bookmarked links. */}
          <Route path="/dashboard/settings" element={<Navigate to="/dashboard" replace />} />
          <Route path="/admin" element={<Protected roles={["admin"]}><Admin /></Protected>} />
          <Route path="/handler" element={<Protected roles={["case_handler", "admin"]}><Handler /></Protected>} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* iter79 (Item G) - Shareable Moment link. Public preview,
             respects auth: logged-in students auto-bounce into Manifest. */}
          <Route path="/m/:id" element={<MomentPublic />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {loc.pathname === "/" ? <HomeFooter /> : <SiteFooter />}
    </div>
    </TelemetryProvider>
  );
}

function HomeFooter() {
  const cols = [
    { title: "Product", links: [
      ["Northstar",   "/#section-compass"],
      ["Cohort",  "/#section-manifest"],
      ["Concierge", "/#section-concierge"],
      ["Pricing",   "/pricing"],
    ]},
    { title: "Company", links: [["Contact","/contact"]] },
    { title: "Legal",   links: [
      ["Privacy","/privacy"],
      ["Terms","/terms"],
      ["Security","/security"],
      ["Refund & Cancellation","/refund-cancellation"],
    ] },
  ];
  return (
    <footer
      className="bg-ink text-paper"
      data-testid="home-footer"
      style={{ background: "#0A0A0A" }}
    >
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid gap-12 md:gap-16 md:grid-cols-12">
        {/* brand */}
        <div className="md:col-span-5">
          <div className="text-2xl md:text-3xl font-bold tracking-[-0.03em] text-paper">Ministry of Exchange</div>
          <p className="mt-3 text-sm md:text-base text-paper/70 max-w-sm leading-relaxed">
            Visa &amp; relocation for exchange semesters abroad.
          </p>
        </div>
        {/* link columns */}
        <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8 md:gap-12">
          {cols.map((c) => (
            <div key={c.title}>
              <div className="text-xs uppercase tracking-[0.2em] text-paper/50 font-bold">{c.title}</div>
              <ul className="mt-5 space-y-3">
                {c.links.map(([label, to]) => {
                  const isHash = to.includes("#");
                  const El = isHash ? "a" : Link;
                  const props = isHash ? { href: to } : { to };
                  return (
                    <li key={label}>
                      <El
                        {...props}
                        className="text-[15px] font-medium text-paper/85 hover:text-[color:var(--moe-cyan)] transition-colors"
                      >
                        {label}
                      </El>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-paper/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-[11px] md:text-xs text-paper/45">
          <div>
            © {new Date().getFullYear()} <span className="font-semibold text-paper/70">Nikshala AI</span>. Ministry of Exchange is a sister product under the Nikshala AI family.
          </div>
          <div>No visa outcome is ever guaranteed. Every rule sourced from official government sites.</div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RoutedShell />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
